import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { expensesApi, vehiclesApi, driversApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Receipt, Trash2, Loader2, TrendingUp, TrendingDown, Edit } from 'lucide-react';

const CAPEX_CATEGORIES = {
  'Vehicle Purchase': ['Base vehicle cost', 'RTO registration', 'Road tax', 'Number plate', 'Hypothecation'],
  'Insurance & Compliance': ['First year insurance', 'Zero dep add-on', 'Fitness certificate', 'Permit fees'],
  'Accessories': ['Seat covers', 'Floor mats', 'Sunfilm', 'Dashcam', 'GPS tracker', 'Music system'],
  'Technology': ['GPS device', 'Telematics hardware', 'FASTag', 'Tablet/Phone'],
  'Branding': ['Vehicle branding', 'Decals', 'PPF coating', 'Ceramic coating'],
};

const OPEX_CATEGORIES = {
  'Fuel & Energy': ['Petrol', 'Diesel', 'CNG', 'EV charging', 'AdBlue'],
  'Driver': ['Salary', 'Revenue share', 'Overtime', 'Allowances', 'Training', 'Uniforms'],
  'Maintenance': ['Engine oil', 'Filters', 'Coolant', 'Brake fluid', 'Repairs', 'AC service'],
  'Wear & Tear': ['Tyres', 'Brake pads', 'Battery', 'Wiper blades', 'Alignment'],
  'Compliance': ['Insurance renewal', 'Road tax', 'Fitness', 'PUC', 'Permit renewal', 'Challans'],
  'Loan & Finance': ['EMI', 'Interest', 'Late fees'],
  'Office': ['Rent', 'Electricity', 'Internet', 'Staff salaries', 'Supplies'],
  'Trip Expenses': ['Toll', 'Parking', 'Driver hotel', 'Driver food'],
};

const PAYMENT_MODES = ['cash', 'bank', 'card', 'upi', 'cheque'];

const initialFormState = {
  expense_type: 'opex',
  category: '',
  sub_category: '',
  vehicle_id: '',
  driver_id: '',
  vendor_name: '',
  amount: '',
  gst_amount: '',
  payment_mode: '',
  expense_date: '',
  description: '',
  invoice_number: '',
  is_recurring: false,
  recurring_frequency: '',
};

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  const fetchData = async () => {
    try {
      const [expensesRes, vehiclesRes, driversRes] = await Promise.all([
        expensesApi.getAll(),
        vehiclesApi.getAll(),
        driversApi.getAll(),
      ]);
      setExpenses(expensesRes.data);
      setVehicles(vehiclesRes.data);
      setDrivers(driversRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => {
      const newState = { ...prev, [name]: value };
      if (name === 'category') {
        newState.sub_category = '';
      }
      return newState;
    });
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      expense_type: expense.expense_type,
      category: expense.category,
      sub_category: expense.sub_category,
      vehicle_id: expense.vehicle_id || '',
      driver_id: expense.driver_id || '',
      vendor_name: expense.vendor_name || '',
      amount: expense.amount?.toString() || '',
      gst_amount: expense.gst_amount?.toString() || '',
      payment_mode: expense.payment_mode || '',
      expense_date: expense.expense_date || '',
      description: expense.description || '',
      invoice_number: expense.invoice_number || '',
      is_recurring: expense.is_recurring || false,
      recurring_frequency: expense.recurring_frequency || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount),
        gst_amount: parseFloat(formData.gst_amount) || 0,
        vehicle_id: formData.vehicle_id === 'none' ? '' : formData.vehicle_id,
      };

      if (editingExpense) {
        await expensesApi.update(editingExpense.id, data);
        toast.success('Expense updated successfully');
      } else {
        await expensesApi.create(data);
        toast.success('Expense added successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData(initialFormState);
    setEditingExpense(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;

    try {
      await expensesApi.delete(id);
      toast.success('Expense deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const filteredExpenses = expenses.filter((e) => {
    if (activeTab === 'all') return true;
    return e.expense_type === activeTab;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const totalCapex = expenses
    .filter((e) => e.expense_type === 'capex')
    .reduce((sum, e) => sum + (e.amount || 0) + (e.gst_amount || 0), 0);

  const totalOpex = expenses
    .filter((e) => e.expense_type === 'opex')
    .reduce((sum, e) => sum + (e.amount || 0) + (e.gst_amount || 0), 0);

  const categories = formData.expense_type === 'capex' ? CAPEX_CATEGORIES : OPEX_CATEGORIES;
  const subCategories = formData.category ? categories[formData.category] || [] : [];

  return (
    <Layout
      title="Expenses"
      subtitle="CAPEX & OPEX Management"
      actions={
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-[#D4AF37] hover:bg-[#B5952F] text-black" data-testid="add-expense-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Expense
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
                {editingExpense ? 'Edit Expense' : 'Add New Expense'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Expense Type *</Label>
                  <Select
                    value={formData.expense_type}
                    onValueChange={(v) => handleSelectChange('expense_type', v)}
                  >
                    <SelectTrigger data-testid="expense-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="capex">CAPEX (Capital)</SelectItem>
                      <SelectItem value="opex">OPEX (Operational)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(v) => handleSelectChange('category', v)}
                  >
                    <SelectTrigger data-testid="expense-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(categories).map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Sub-Category *</Label>
                  <Select
                    value={formData.sub_category}
                    onValueChange={(v) => handleSelectChange('sub_category', v)}
                    disabled={!formData.category}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select sub-category" />
                    </SelectTrigger>
                    <SelectContent>
                      {subCategories.map((sub) => (
                        <SelectItem key={sub} value={sub}>
                          {sub}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Vehicle (Optional)</Label>
                  <Select
                    value={formData.vehicle_id || 'none'}
                    onValueChange={(v) => handleSelectChange('vehicle_id', v === 'none' ? '' : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select vehicle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {vehicles.map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.registration_number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="amount">Amount (₹) *</Label>
                  <Input
                    id="amount"
                    name="amount"
                    type="number"
                    placeholder="Enter amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gst_amount">GST Amount (₹)</Label>
                  <Input
                    id="gst_amount"
                    name="gst_amount"
                    type="number"
                    placeholder="GST amount"
                    value={formData.gst_amount}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Payment Mode *</Label>
                  <Select
                    value={formData.payment_mode}
                    onValueChange={(v) => handleSelectChange('payment_mode', v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select mode" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_MODES.map((mode) => (
                        <SelectItem key={mode} value={mode} className="capitalize">
                          {mode.toUpperCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expense_date">Date *</Label>
                  <Input
                    id="expense_date"
                    name="expense_date"
                    type="date"
                    value={formData.expense_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vendor_name">Vendor Name</Label>
                  <Input
                    id="vendor_name"
                    name="vendor_name"
                    placeholder="Vendor/Supplier name"
                    value={formData.vendor_name}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invoice_number">Invoice Number</Label>
                  <Input
                    id="invoice_number"
                    name="invoice_number"
                    placeholder="Invoice/Bill number"
                    value={formData.invoice_number}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    name="description"
                    placeholder="Additional notes"
                    value={formData.description}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#D4AF37] hover:bg-[#B5952F] text-black"
                  data-testid="expense-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : editingExpense ? (
                    'Update Expense'
                  ) : (
                    'Add Expense'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      <div className="space-y-6" data-testid="expenses-page">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total CAPEX</p>
                  <p className="text-2xl font-bold text-blue-600 tabular-nums">
                    {formatCurrency(totalCapex)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total OPEX</p>
                  <p className="text-2xl font-bold text-orange-600 tabular-nums">
                    {formatCurrency(totalOpex)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <TrendingDown className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Expenses</p>
                  <p className="text-2xl font-bold text-slate-900 tabular-nums">
                    {formatCurrency(totalCapex + totalOpex)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                  <Receipt className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs & Table */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all" data-testid="tab-all">All Expenses</TabsTrigger>
            <TabsTrigger value="capex" data-testid="tab-capex">CAPEX</TabsTrigger>
            <TabsTrigger value="opex" data-testid="tab-opex">OPEX</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
                      </TableCell>
                    </TableRow>
                  ) : filteredExpenses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12">
                        <Receipt className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                        <p className="text-slate-500">No expenses found</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredExpenses.map((expense) => (
                      <TableRow key={expense.id} className="hover:bg-slate-50">
                        <TableCell>{expense.expense_date}</TableCell>
                        <TableCell>
                          <Badge className={expense.expense_type === 'capex' 
                            ? 'bg-blue-100 text-blue-700 border-blue-200'
                            : 'bg-orange-100 text-orange-700 border-orange-200'
                          }>
                            {expense.expense_type.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{expense.category}</p>
                            <p className="text-sm text-slate-500">{expense.sub_category}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {expense.vehicle_id 
                            ? vehicles.find(v => v.id === expense.vehicle_id)?.registration_number || '-'
                            : '-'
                          }
                        </TableCell>
                        <TableCell className="tabular-nums">
                          <div>
                            <p className="font-medium">{formatCurrency(expense.amount)}</p>
                            {expense.gst_amount > 0 && (
                              <p className="text-xs text-slate-500">+GST: {formatCurrency(expense.gst_amount)}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="uppercase">{expense.payment_mode}</TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(expense)}
                              className="text-blue-500 hover:text-blue-700"
                              data-testid={`edit-expense-${expense.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(expense.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Expenses;
